/**
 * Component แสดงผลรางวัลหวย — ใช้ร่วมกันระหว่าง หน้าแรก (/) และ หน้า lobby-huay/result
 * ดึงข้อมูล: useLotteryResults (Public API), UI แบบ user-modern + wip_wup
 */
import React, { useState, useEffect } from 'react'
import { useLotteryResults } from '../../hooks/useLotteryResults'
import { getWinningNumbers, hasAnyResult, displayValue, getRoundDrawLabel, isRoundCancelled } from '../../utils/resultDisplayUtils'
import { getRoundImage } from '../../services/lotteryService'
import type { LotteryRound } from '../../services/lotteryService'
import './ModernLotteryResultSections.css'

function todayBangkokYYYYMMDD(): string {
  const f = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Bangkok',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
  return f.format(new Date())
}

function formatYeekeeRoundLabel(roundNumber: string | undefined): string {
  if (!roundNumber) return ''
  const parts = roundNumber.split('-')
  const suffix = parts[parts.length - 1]
  if (/^\d+$/.test(suffix)) return String(parseInt(suffix, 10))
  return roundNumber
}

/** รอบ HH:mm น. จาก betEndTime = เวลาปิดรับ/ออกผล (Bangkok) — ต้องตรงกับหน้าโพย */
function getYeekeeRoundTimeLabel(round: LotteryRound | null): string {
  if (!round?.betEndTime) return 'รอบ -'
  const t = new Date(round.betEndTime).toLocaleTimeString('th-TH', {
    timeZone: 'Asia/Bangkok',
    hour: '2-digit',
    minute: '2-digit',
  })
  return `รอบ ${t} น.`
}

function DrawDateBadge({ label, className = '' }: { label: string; className?: string }) {
  return (
    <span
      className={`lottery-result-draw-badge bg_secondary border_10 px-2 py-1 c_secondary small flex-shrink-0 ${className}`.trim()}
    >
      {label}
    </span>
  )
}

function CancelledResultStatus() {
  return (
    <div className="col-12 px-1 text-center lottery-result-cancelled-status">
      <h5 className="wrap_text_h font_md c_warning mb-0">ยกเลิก</h5>
    </div>
  )
}

/** จำนวนการ์ดสูงสุดที่แสดงต่อ section (เลขชุด, ต่างประเทศ, หุ้น, VIP, VISA) — แสดงให้ครบตามข้อมูลที่มี */
const MAX_CARDS_PER_SECTION = 24

/** URL รูปหวย: ใช้ huayImageURL จาก API ถ้าเป็น full URL ไม่เช่นนั้นใช้ getAssetUrl(path) */
function getRoundImageSrc(
  round: LotteryRound | null,
  getAssetUrl: (path: string) => string,
  fallbackPath: string
): string {
  const urlOrPath = round ? getRoundImage(round) : fallbackPath
  if (typeof urlOrPath === 'string' && /^https?:\/\//i.test(urlOrPath)) return urlOrPath
  return getAssetUrl(urlOrPath || fallbackPath)
}

export interface ModernLotteryResultSectionsProps {
  /** tenant ID — ถ้าส่งมา hook จะ guard ไม่ fetch จนกว่า tenantId จะพร้อม */
  tenantId?: string
  /** วันที่เลือก (YYYY-MM-DD) — ถ้าไม่ส่ง จะใช้วันนี้ (Bangkok) */
  selectedDate?: string
  /** อัปเดตเมื่อผู้ใช้เปลี่ยนวันที่ (ใช้เมื่อ showDatePicker = true) */
  onSelectedDateChange?: (date: string) => void
  /** แสดงช่องเลือกวันที่ + ปุ่มตกลง */
  showDatePicker?: boolean
  /** แสดงหัวข้อ "ผลรางวัล" ด้านบน */
  showTitle?: boolean
  /** ฟังก์ชัน resolve URL รูป (จาก theme/asset) */
  getAssetUrl: (path: string) => string
  /** onError สำหรับ img */
  handleImgError: (e: React.SyntheticEvent<HTMLImageElement>) => void
  /** โหมดดึง round เดียว (จาก query/state) */
  targetRoundId?: string | null
  /** แสดงข้อความ error เมื่อ API ล้มเหลว */
  showPoolError?: boolean
  /** คลิกรอบยี่กี VIP → ไปหน้าผลยี่กี (เมื่อ login) */
  onYeekeeRoundClick?: (round: LotteryRound) => void
  /** className ของ wrapper หลัก */
  className?: string
  /** นาทีที่เหลือก่อนปิดรับ — แสดง wip_wup สำหรับหวยยี่กี (จากแอดมิน ตั้งค่าอื่นๆ), 1–120, default 15 */
  wipWupMinutes?: number
}

const ModernLotteryResultSections: React.FC<ModernLotteryResultSectionsProps> = ({
  tenantId,
  selectedDate: selectedDateProp,
  onSelectedDateChange,
  showDatePicker = true,
  showTitle = false,
  getAssetUrl,
  handleImgError,
  targetRoundId = null,
  showPoolError = true,
  onYeekeeRoundClick,
  className = '',
  wipWupMinutes = 15,
}) => {
  const [refreshTrigger, setRefreshTrigger] = useState(0)
  const [selectedDateState, setSelectedDateState] = useState(todayBangkokYYYYMMDD)
  const selectedDate = selectedDateProp ?? selectedDateState
  const setSelectedDate = onSelectedDateChange ?? setSelectedDateState

  useEffect(() => {
    const onHuayTypeUpdated = () => setRefreshTrigger((t) => t + 1)
    window.addEventListener('huay_type_updated', onHuayTypeUpdated)
    return () => {
      window.removeEventListener('huay_type_updated', onHuayTypeUpdated)
    }
  }, [])

  const {
    poolError,
    latestGovernmentRound,
    latestYeekeeRound,
    latestEgovRound,
    latestGsbRound,
    latestSetRounds,
    latestThaiStockRounds,
    latestForeignRounds,
    latestForeignStockRounds,
    latestVipRounds,
    latestVisaRounds,
    allYeekeeVipRounds,
  } = useLotteryResults(selectedDate, { targetRoundId, refreshTrigger }, tenantId)

  const gov = latestGovernmentRound
  const govWn = getWinningNumbers(gov)
  const govHasResult = hasAnyResult(gov)
  const govCancelled = isRoundCancelled(gov)
  const dispGov = (v: string) => displayValue(v, { isCancelled: govCancelled, hasResult: govHasResult })

  const renderCard3x2 = (
    round: LotteryRound | null,
    title: string,
    flagImg: string,
    drawLabel: string
  ) => {
    const wn = getWinningNumbers(round)
    const hasResult = hasAnyResult(round)
    const isCancelled = isRoundCancelled(round)
    const disp = (v: string) => displayValue(v, { isCancelled, hasResult })
    return (
      <div className={`row mx__1 lottery-result-card-metrics${isCancelled ? ' lottery-result-card--cancelled' : ''}`}>
        <div className="col-12 px-1">
          <div className="h_box lottery-result-card-head d-flex flex-nowrap align-items-center justify-content-between">
            <div className="lottery-result-card-head__main d-flex align-items-center min-w-0">
              <div className="lottery-result-huay-icon bx_sdp">
                <img src={getRoundImageSrc(round, getAssetUrl, flagImg)} alt="" onError={handleImgError} />
              </div>
              <h6 className="c_tertiary mb-0 lottery-result-card-head__title" title={title}>{title}</h6>
            </div>
            <DrawDateBadge label={drawLabel} />
          </div>
        </div>
        {isCancelled ? (
          <CancelledResultStatus />
        ) : (
          <>
            <div className="col px-1 text-center bg_m lottery-result-metric">
              <h6 className="head_text font_md c_tertiary">3 ตัวบน</h6>
              <h5 className="wrap_text_h font_md bg_secondary">{disp(wn.threeUp)}</h5>
            </div>
            <div className="col px-1 text-center bg_m lottery-result-metric">
              <h6 className="head_text font_md c_tertiary">2 ตัวล่าง</h6>
              <h5 className="wrap_text_h font_md bg_secondary">{disp(wn.twoDown)}</h5>
            </div>
          </>
        )}
      </div>
    )
  }

  const now = Date.now()

  return (
    <div className={`modern-lottery-result-sections ${className}`.trim()} style={{ rowGap: '0.5rem' }}>
      {showTitle && (
        <div className="col-12 px-1 c_quinary text-center">
          <h3 className="c_tertiary font_md">ผลรางวัล</h3>
        </div>
      )}

      {showPoolError && poolError && (
        <div className="col-12 px-1">
          <div className="border_5 bdc_primary bg_op_secondary p-2 mt-2">
            <p className="c_primary mb-0 small">{poolError}</p>
          </div>
        </div>
      )}

      {showDatePicker && (
        <div className="col-12 px-1 lottery-result-date-picker">
          <div className="d-flex align-items-center">
            <input
              type="date"
              className="form-control border_5 bg_op_secondary c_tertiary"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              max={todayBangkokYYYYMMDD()}
            />
          </div>
        </div>
      )}

      {/* หวยรัฐบาลไทย + หวยยี่กี — แถวเดียวกัน (layout ตามรูป) */}
      <div className="row mx__1 mt-2" style={{ rowGap: '0.5rem' }}>
        {/* หวยรัฐบาลไทย */}
        <div className={`col-12 px-1 ${latestYeekeeRound ? 'col-md-6' : ''}`}>
          <div className="h_box d-flex flex-nowrap align-items-center justify-content-between bg_op_secondary border_10 px-2">
            <div className="d-flex align-items-center min-w-0 flex-grow-1">
              <div className="lottery-result-huay-icon bx_sdp">
                <img src={getRoundImageSrc(gov, getAssetUrl, '/img/flag/th.png')} alt="" onError={handleImgError} />
              </div>
              <h5 className="font_md c_tertiary ms-2 mb-0">หวยรัฐบาลไทย</h5>
            </div>
            <DrawDateBadge label={getRoundDrawLabel(gov, selectedDate)} />
          </div>
          <div className="bd_box border_10">
            <div className={`box_pd_5 p-2 bg_op_secondary border_10 bx_sd${govCancelled ? ' lottery-result-card--cancelled' : ''}`}>
              <div className={`row mx__1 g-2 lottery-result-gov-metrics${govCancelled ? ' lottery-result-card--cancelled' : ''}`}>
                {govCancelled ? (
                  <CancelledResultStatus />
                ) : (
                  <>
                    <div className="col-12 px-1 text-center lottery-result-gov-first">
                      <h6 className="head_text font_md c_tertiary mb-1">รางวัลที่ 1</h6>
                      <h5 className="wrap_text_h font_md t-l bg_secondary c_secondary mb-0">{dispGov(govWn.first)}</h5>
                    </div>
                    <div className="col-4 px-1 text-center">
                      <h6 className="head_text font_md c_tertiary mb-1">3 ตัวบน</h6>
                      <h5 className="wrap_text_h font_md bg_secondary c_secondary mb-0">{dispGov(govWn.threeUp)}</h5>
                    </div>
                    <div className="col-4 px-1 text-center">
                      <h6 className="head_text font_md c_tertiary mb-1">2 ตัวล่าง</h6>
                      <h5 className="wrap_text_h font_md bg_secondary c_secondary mb-0">{dispGov(govWn.twoDown)}</h5>
                    </div>
                    <div className="col-4 px-1 text-center">
                      <h6 className="head_text font_md c_tertiary mb-1">3 ตัวล่าง</h6>
                      <h5 className="wrap_text_h font_md bg_secondary c_secondary mb-0">{dispGov(govWn.threeDown)}</h5>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* หวยยี่กี */}
        {latestYeekeeRound && (() => {
          const yeekeeWn = getWinningNumbers(latestYeekeeRound)
          const yeekeeHasResult = hasAnyResult(latestYeekeeRound)
          const yeekeeCancelled = isRoundCancelled(latestYeekeeRound)
          const dispYeekee = (v: string) => displayValue(v, { isCancelled: yeekeeCancelled, hasResult: yeekeeHasResult })
          const roundLabel = formatYeekeeRoundLabel(latestYeekeeRound.roundNumber)
          const timeStr = latestYeekeeRound?.betEndTime
            ? new Date(latestYeekeeRound.betEndTime).toLocaleTimeString('th-TH', {
                timeZone: 'Asia/Bangkok',
                hour: '2-digit',
                minute: '2-digit',
              }) + ' น.'
            : '-'
          return (
            <div className="col-12 col-md-6 px-1">
              <div className="h_box d-flex flex-nowrap align-items-center justify-content-between bg_op_secondary border_10 px-2">
                <div className="d-flex align-items-center min-w-0 flex-grow-1">
                  <div className="lottery-result-huay-icon bx_sdp">
                    <img src={getRoundImageSrc(latestYeekeeRound, getAssetUrl, '/img/flag/th.png')} alt="" onError={handleImgError} />
                  </div>
                  <h5 className="font_md c_tertiary ms-2 mb-0">หวยยี่กี</h5>
                </div>
                <span className="lottery-result-draw-badge bg_secondary border_10 px-2 py-1 c_secondary small flex-shrink-0 ms-2">
                  {getYeekeeRoundTimeLabel(latestYeekeeRound)}
                </span>
              </div>
              <div className="bd_box border_10">
                <div className={`box_pd_5 p-2 bg_op_secondary border_10 bx_sd${yeekeeCancelled ? ' lottery-result-card--cancelled' : ''}`}>
                  <div className={`row mx__1 g-2 lottery-result-metrics-2col${yeekeeCancelled ? ' lottery-result-card--cancelled' : ''}`}>
                    <div className="col-12 px-1 text-center">
                      <h6 className="head_text font_md c_tertiary mb-1 lottery-round-label">รอบที่ {roundLabel}</h6>
                      <h5 className="wrap_text_h font_md bg_secondary c_secondary mb-0">เวลา :{timeStr}</h5>
                    </div>
                    {yeekeeCancelled ? (
                      <CancelledResultStatus />
                    ) : (
                      <>
                        <div className="col-6 px-1 text-center">
                          <h6 className="head_text font_md c_tertiary mb-1">3 ตัวบน</h6>
                          <h5 className="wrap_text_h font_md bg_secondary c_secondary mb-0">{dispYeekee(yeekeeWn.threeUp)}</h5>
                        </div>
                        <div className="col-6 px-1 text-center">
                          <h6 className="head_text font_md c_tertiary mb-1">2 ตัวล่าง</h6>
                          <h5 className="wrap_text_h font_md bg_secondary c_secondary mb-0">{dispYeekee(yeekeeWn.twoDown)}</h5>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
        })()}
      </div>

      {/* หวยธนาคาร (ธ.ก.ส. / ออมสิน) */}
      <div className="row mx__1 mt-2" style={{ rowGap: '0.5rem' }}>
        <div className="col-12 px-1 lottery-result-section-head">
          <div className="h_box">
            <div className="d-flex align-items-center">
              <h5 className="font_md c_tertiary">หวยธนาคาร</h5>
            </div>
          </div>
        </div>
        {latestEgovRound && (
          <div className="col-12 col-md-6 px-1">
            <div className="bd_box border_10">
              <div className="box_pd_5 p-2 bg_op_secondary border_10 bx_sd">
                {renderCard3x2(
                  latestEgovRound,
                  (latestEgovRound.huayName as string) ?? 'หวยธ.ก.ส.',
                  '/img/flag/th.png',
                  getRoundDrawLabel(latestEgovRound, selectedDate)
                )}
              </div>
            </div>
          </div>
        )}
        {latestGsbRound && (
          <div className="col-12 col-md-6 px-1">
            <div className="bd_box border_10">
              <div className="box_pd_5 p-2 bg_op_secondary border_10 bx_sd">
                {renderCard3x2(
                  latestGsbRound,
                  (latestGsbRound.huayName as string) ?? 'หวยออมสิน',
                  '/img/flag/th.png',
                  getRoundDrawLabel(latestGsbRound, selectedDate)
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* หวยเลขชุด */}
      <div className="row mx__1 mt-2" style={{ rowGap: '0.5rem' }}>
        <div className="col-12 px-1 lottery-result-section-head">
          <div className="h_box">
            <div className="d-flex align-items-center">
              <h5 className="font_md c_tertiary">หวยเลขชุด</h5>
            </div>
          </div>
        </div>
        {(latestSetRounds.length > 0 ? latestSetRounds.slice(0, MAX_CARDS_PER_SECTION) : [null, null, null, null]).map(
          (round, i) => {
            const r = round ?? null
            const wn = getWinningNumbers(r)
            const hasResult = hasAnyResult(r)
            const isCancelled = isRoundCancelled(r)
            const disp = (v: string) => displayValue(v, { isCancelled, hasResult })
            return (
              <div key={r?._id ?? `set-${i}`} className="col-12 col-md-6 px-1">
                <div className="bd_box border_10">
                  <div className={`box_pd_5 p-2 bg_op_secondary border_10 bx_sd${isCancelled ? ' lottery-result-card--cancelled' : ''}`}>
                    <div className={`row mx__1 lottery-result-card-metrics${isCancelled ? ' lottery-result-card--cancelled' : ''}`}>
                      <div className="col-12 px-1">
                        <div className="h_box lottery-result-card-head d-flex flex-nowrap align-items-center justify-content-between">
                          <div className="lottery-result-card-head__main d-flex align-items-center min-w-0">
                            <div className="lottery-result-huay-icon bx_sdp">
                              <img src={getRoundImageSrc(r, getAssetUrl, '/img/flag/th.png')} alt="" onError={handleImgError} />
                            </div>
                            <h6 className="c_tertiary mb-0 lottery-result-card-head__title" title={r?.huayName ?? 'หวยชุดรัฐบาลไทย'}>{r?.huayName ?? 'หวยชุดรัฐบาลไทย'}</h6>
                          </div>
                          <DrawDateBadge label={getRoundDrawLabel(r, selectedDate)} />
                        </div>
                      </div>
                      {isCancelled ? (
                        <CancelledResultStatus />
                      ) : (
                        <div className="col px-1 text-center bg_m lottery-result-metric">
                          <h6 className="head_text font_md c_tertiary">เลขชุด 4 ตัว</h6>
                          <h5 className="wrap_text_h font_md bg_secondary">{disp(wn.fourUp)}</h5>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )
          }
        )}
      </div>

      {/* หวยหุ้นไทย */}
      <div className="row mx__1 mt-2" style={{ rowGap: '0.5rem' }}>
        <div className="col-12 px-1 lottery-result-section-head">
          <div className="h_box">
            <div className="d-flex align-items-center">
              <h5 className="font_md c_tertiary">หวยหุ้นไทย</h5>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-6 px-1">
          <div className="bd_box border_10">
            <div className="box_pd_5 p-2 bg_op_secondary border_10 bx_sd">
              {renderCard3x2(
                latestThaiStockRounds[0] ?? null,
                (latestThaiStockRounds[0]?.huayName as string) ?? 'หวยหุ้นไทยปิดเย็น',
                '/img/flag/th.png',
                getRoundDrawLabel(latestThaiStockRounds[0] ?? null, selectedDate)
              )}
            </div>
          </div>
        </div>
      </div>

      {/* หวยต่างประเทศ */}
      {latestForeignRounds.length > 0 && (
      <div className="row mx__1 mt-2" style={{ rowGap: '0.5rem' }}>
        <div className="col-12 px-1 lottery-result-section-head">
          <div className="h_box">
            <div className="d-flex align-items-center">
              <h5 className="font_md c_tertiary">หวยต่างประเทศ</h5>
            </div>
          </div>
        </div>
        {latestForeignRounds.slice(0, MAX_CARDS_PER_SECTION).map((round) => (
              <div key={round._id} className="col-12 col-md-4 px-1">
                <div className="bd_box border_10">
                  <div className="box_pd_5 p-2 bg_op_secondary border_10 bx_sd">
                    {renderCard3x2(
                      round,
                      (round.huayName as string) ?? 'หวยลาวประตูชัย',
                      '/img/flag/la.png',
                      getRoundDrawLabel(round, selectedDate)
                    )}
                  </div>
                </div>
              </div>
        ))}
      </div>
      )}

      {/* หวยหุ้น */}
      {latestForeignStockRounds.length > 0 && (
        <div className="row mx__1 mt-2" style={{ rowGap: '0.5rem' }}>
          <div className="col-12 px-1 lottery-result-section-head">
            <div className="h_box">
              <div className="d-flex align-items-center">
                <h5 className="font_md c_tertiary">หวยหุ้น</h5>
              </div>
            </div>
          </div>
          {latestForeignStockRounds.slice(0, MAX_CARDS_PER_SECTION).map((round) => (
            <div key={round._id} className="col-12 col-md-4 px-1">
              <div className="bd_box border_10">
                <div className="box_pd_5 p-2 bg_op_secondary border_10 bx_sd">
                  {renderCard3x2(
                    round,
                    (round?.huayName as string) ?? 'หวยหุ้น',
                    '/img/flag/la.png',
                    getRoundDrawLabel(round, selectedDate)
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* หวย VIP */}
      {latestVipRounds.length > 0 && (
        <div className="row mx__1 mt-2" style={{ rowGap: '0.5rem' }}>
          <div className="col-12 px-1 lottery-result-section-head">
            <div className="h_box">
              <div className="d-flex align-items-center">
                <h5 className="font_md c_tertiary">หวย VIP</h5>
              </div>
            </div>
          </div>
          {latestVipRounds.slice(0, MAX_CARDS_PER_SECTION).map((round) => (
            <div key={round._id} className="col-12 col-md-4 px-1">
              <div className="bd_box border_10">
                <div className="box_pd_5 p-2 bg_op_secondary border_10 bx_sd">
                  {renderCard3x2(
                    round,
                    (round?.huayName as string) ?? 'หวย VIP',
                    '/img/flag/la.png',
                    getRoundDrawLabel(round, selectedDate)
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* หวย VISA */}
      {latestVisaRounds.length > 0 && (
        <div className="row mx__1 mt-2" style={{ rowGap: '0.5rem' }}>
          <div className="col-12 px-1 lottery-result-section-head">
            <div className="h_box">
              <div className="d-flex align-items-center">
                <h5 className="font_md c_tertiary">หวย VISA</h5>
              </div>
            </div>
          </div>
          {latestVisaRounds.slice(0, MAX_CARDS_PER_SECTION).map((round) => (
            <div key={round._id} className="col-12 col-md-4 px-1">
              <div className="bd_box border_10">
                <div className="box_pd_5 p-2 bg_op_secondary border_10 bx_sd">
                  {renderCard3x2(
                    round,
                    (round?.huayName as string) ?? 'หวย VISA',
                    '/img/flag/la.png',
                    getRoundDrawLabel(round, selectedDate)
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* หวยยี่กี VIP */}
      {allYeekeeVipRounds.length > 0 && (
        <div className="row mx__1 mt-2" style={{ rowGap: '0.5rem' }}>
          <div className="col-12 px-1 lottery-result-section-head">
            <div className="h_box">
              <div className="d-flex align-items-center">
                <h5 className="font_md c_tertiary">หวยยี่กี VIP</h5>
              </div>
            </div>
          </div>
          <div className="col-12 px-1">
            <div className="bd_box border_10 p-2 bg_op_secondary border_10 bx_sd">
              <div className="row mx__1 lottery-result-yeekee-vip-grid" style={{ rowGap: '0.5rem' }}>
                {[...allYeekeeVipRounds]
                  .sort((a, b) => new Date(a.betStartTime).getTime() - new Date(b.betStartTime).getTime())
                  .map((round) => {
                    const betStart = new Date(round.betStartTime).getTime()
                    const betEnd = new Date(round.betEndTime).getTime()
                    const isOpen = round.status === 'open' && round.isOpen !== false
                    const isInBetWindow = isOpen && now >= betStart && now <= betEnd
                    const minutesRemaining = (betEnd - now) / (60 * 1000)
                    const isLastMinutes = isInBetWindow && minutesRemaining <= wipWupMinutes && minutesRemaining > 0
                    const isFuture = isOpen && now < betStart
                    const isCancelled = round.status === 'cancelled'
                    const isClosed = !isFuture && (!isOpen || now > betEnd) && !isCancelled
                    const res = (round as { yeekee?: { result?: { threeDigitTop?: string; twoDigitDown?: string; twoDigitTop?: string } } }).yeekee?.result
                    const threeUp = res?.threeDigitTop ?? ''
                    const twoDown = res?.twoDigitDown ?? res?.twoDigitTop ?? ''
                    // Draw time = betEndTime, matching slip pages
                    const drawTime = new Date(round.betEndTime).toLocaleTimeString('th-TH', {
                      timeZone: 'Asia/Bangkok',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                    const roundLabel = formatYeekeeRoundLabel(round.roundNumber)
                    const displayThreeUp = isCancelled ? 'ยกเลิก' : isFuture ? 'รอผล' : (threeUp || 'รอผล')
                    const displayTwoDown = isCancelled ? 'ยกเลิก' : isFuture ? 'รอผล' : (twoDown || 'รอผล')
                    const isClickable = !!onYeekeeRoundClick && round.status !== 'open'
                    return (
                      <div key={round._id} className="col-6 col-md-4 col-lg-3 px-1 lottery-result-yeekee-vip-item">
                        <div
                          className={`bd_box border_10 p-2 bx_sd text-center lottery-result-yeekee-vip-card ${isCancelled ? 'h_cancelled' : ''} ${isClosed ? 'h_close' : ''} ${isLastMinutes ? 'wip_wup' : ''}`}
                          style={isClickable ? { cursor: 'pointer' } : undefined}
                          onClick={() => isClickable && onYeekeeRoundClick?.(round)}
                          onKeyDown={(e) => isClickable && e.key === 'Enter' && onYeekeeRoundClick?.(round)}
                          role={isClickable ? 'button' : undefined}
                          tabIndex={isClickable ? 0 : undefined}
                        >
                          <h6 className="c_tertiary font_md mb-1 lottery-round-label">รอบที่ {roundLabel}</h6>
                          <small className="c_tertiary d-block mb-1">{drawTime} น.</small>
                          <div className="row mx__1 g-1 lottery-result-yeekee-vip-metrics">
                            {isCancelled ? (
                              <div className="col-12 px-1 lottery-result-cancelled-status">
                                <h5 className="wrap_text_h font_md c_warning mb-0">ยกเลิก</h5>
                              </div>
                            ) : (
                              <>
                                <div className="col px-1">
                                  <h6 className="head_text font_md c_tertiary small">3 ตัวบน</h6>
                                  <h5 className="wrap_text_h font_md bg_secondary">{displayThreeUp}</h5>
                                </div>
                                <div className="col px-1">
                                  <h6 className="head_text font_md c_tertiary small">2 ตัวล่าง</h6>
                                  <h5 className="wrap_text_h font_md bg_secondary">{displayTwoDown}</h5>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ModernLotteryResultSections
